import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, IdCard } from "lucide-react";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [sessionId, setSessionId] = useState(null);
  const [email, setEmail] = useState("");
  const [cf, setCf] = useState("");
  const [needsCf, setNeedsCf] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const finish = (data) => {
    setUser(data);
    window.history.replaceState(null, "", window.location.pathname);
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }
    setSessionId(sid);
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        if (data?.needs_cf) {
          setEmail(data.email || "");
          setNeedsCf(true);
          return;
        }
        finish(data);
      } catch (e) {
        const d = e?.response?.data?.detail;
        const msg = typeof d === "string" ? d : "Accesso con Google non riuscito";
        navigate(`/login?err=${encodeURIComponent(msg)}`, { replace: true });
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submitCf = async (e) => {
    e.preventDefault();
    setError("");
    if (!cf.trim()) { setError("Inserisci il tuo Codice Fiscale"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/google/complete", {
        session_id: sessionId,
        codice_fiscale: cf.trim().toUpperCase(),
      });
      finish(data);
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (needsCf) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0E281F] px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl" data-testid="cf-prompt">
          <div className="mb-5 flex items-center gap-3">
            <img src="/emblem.png" alt="Emblema" className="h-12 w-auto object-contain" />
            <div>
              <h1 className="font-heading text-lg font-extrabold text-[#14382C]">Verifica anagrafica</h1>
              <p className="text-xs text-slate-500">{email}</p>
            </div>
          </div>
          <p className="mb-5 text-sm text-slate-600">
            La tua email non risulta collegata a una scheda in anagrafica. Inserisci il tuo
            <strong> Codice Fiscale</strong> per verificare la corrispondenza.
          </p>
          <form onSubmit={submitCf} className="space-y-4">
            <div className="space-y-2">
              <Label>Codice Fiscale</Label>
              <Input
                data-testid="cf-input"
                value={cf}
                onChange={(e) => setCf(e.target.value)}
                placeholder="RSSMRA80A01H501U"
                className="font-mono-code uppercase"
                autoFocus
              />
            </div>
            {error && <p className="text-sm font-medium text-red-600" data-testid="cf-error">{error}</p>}
            <Button type="submit" disabled={submitting} data-testid="cf-submit" className="w-full bg-[#14382C] hover:bg-[#0E281F]">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <IdCard className="mr-2 h-4 w-4" /> Verifica e accedi
            </Button>
            <Button type="button" variant="ghost" className="w-full text-slate-500" onClick={() => navigate("/login", { replace: true })}>
              Annulla
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
      <img src="/emblem.png" alt="Emblema" className="h-14 w-auto object-contain" />
      <div className="flex items-center gap-2 text-slate-600">
        <Loader2 className="h-5 w-5 animate-spin" /> Accesso in corso…
      </div>
    </div>
  );
}
